/**
 * Gate-area data plumbing. The visitors/services APIs are being built in
 * parallel — every fetch here tolerates 404/501/network failure and surfaces
 * an 'error' state so screens fall back to EmptyState instead of crashing.
 */
import React from 'react';

import { translate, monthShort, dayShort } from '@/i18n';
import { api } from '@/lib/client';

export type LoadState = 'loading' | 'ready' | 'error';

/** 400 "No flat linked to your account" — expected for admin/guard accounts. */
export function isNoFlat(e: unknown): boolean {
  const err = e as { status?: number; error?: string };
  return err?.status === 400 && /no flat/i.test(err?.error ?? '');
}

type Body = Record<string, unknown>;

/** Pull the first array out of a `{items: [...]}`-ish (or bare-array) body. */
export function extractItems<T>(body: unknown, ...keys: string[]): T[] {
  if (Array.isArray(body)) return body as T[];
  if (body && typeof body === 'object') {
    for (const k of ['items', ...keys]) {
      const v = (body as Body)[k];
      if (Array.isArray(v)) return v as T[];
    }
  }
  return [];
}

export function useGateList<T>(path: string, ...altKeys: string[]) {
  const [items, setItems] = React.useState<T[]>([]);
  const [state, setState] = React.useState<LoadState>('loading');

  const reload = React.useCallback(async () => {
    setState('loading');
    try {
      const body = await api.get<unknown>(path);
      setItems(extractItems<T>(body, ...altKeys));
      setState('ready');
    } catch (e) {
      setItems([]);
      // Flat-scoped endpoints answer 400 "No flat linked" for admin/guard
      // accounts — that's a calm empty, not a system error (user report
      // 2026-07-18: admin saw "Couldn't load" walls everywhere).
      setState(isNoFlat(e) ? 'ready' : 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  React.useEffect(() => { void Promise.resolve().then(() => reload()); }, [reload]);

  return { items, setItems, state, reload };
}

/* ---------- tolerant field readers (API shapes may drift) ---------- */
export function str(o: unknown, ...keys: string[]): string | undefined {
  if (!o || typeof o !== 'object') return undefined;
  for (const k of keys) {
    const v = (o as Body)[k];
    if (typeof v === 'string' && v.length) return v;
    if (typeof v === 'number') return String(v);
  }
  return undefined;
}

export function num(o: unknown, ...keys: string[]): number | undefined {
  if (!o || typeof o !== 'object') return undefined;
  for (const k of keys) {
    const v = (o as Body)[k];
    if (typeof v === 'number') return v;
    if (typeof v === 'string' && v && !Number.isNaN(Number(v))) return Number(v);
  }
  return undefined;
}

export function bool(o: unknown, ...keys: string[]): boolean | undefined {
  if (!o || typeof o !== 'object') return undefined;
  for (const k of keys) {
    const v = (o as Body)[k];
    if (typeof v === 'boolean') return v;
  }
  return undefined;
}

/* ---------- date bucketing + display for history lists ---------- */
export type Group = 'Today' | 'Yesterday' | 'This week' | 'Earlier';
export const GROUP_ORDER: Group[] = ['Today', 'Yesterday', 'This week', 'Earlier'];

export function groupFor(iso: string | undefined, fallback?: string): Group {
  if (fallback && (GROUP_ORDER as string[]).includes(fallback)) return fallback as Group;
  if (!iso) return 'Earlier';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Earlier';
  const now = new Date();
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.floor((startOfDay(now) - startOfDay(d)) / 86400000);
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return 'This week';
  return 'Earlier';
}

export function fmtTime(d: Date): string {
  let h = d.getHours();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${String(d.getMinutes()).padStart(2, '0')} ${ampm}`;
}

/** "Today, 2:41 PM" / "Yesterday, 9:02 AM" / "Mon, 1:58 PM" / "3 Oct". */
export function fmtWhen(iso: string | undefined, fallback?: string): string {
  if (!iso) return fallback ?? '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fallback ?? iso;
  const g = groupFor(iso);
  if (g === 'Today') return `${translate('time_today')}, ${fmtTime(d)}`;
  if (g === 'Yesterday') return `${translate('time_yesterday')}, ${fmtTime(d)}`;
  if (g === 'This week') return `${dayShort(d.getDay())}, ${fmtTime(d)}`;
  return `${d.getDate()} ${monthShort(d.getMonth())}`;
}
